'use client'

import { FC } from 'react'
import { useBlogPostsQuery } from '@/app/hooks/queries/useBlogPostsQuery'
import BlogCard from '@/app/components/blog/BlogCard'
import BlogEmptyState from '@/app/components/blog/BlogEmptyState'
import LoadingDots from '@/app/components/common/LoadingDots'
import Button from '@/app/components/common/Button'
import { BlogPost } from '@/app/types/blog'
import { useTranslations } from 'next-intl'

interface Props {
	language: string
	categorySlug?: string
	initialPosts: BlogPost[]
	initialTotal: number
}

const BlogPostsList: FC<Props> = ({
	language,
	categorySlug,
	initialPosts,
	initialTotal,
}) => {
	const t = useTranslations('Blog')
	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError } =
		useBlogPostsQuery({
			language,
			categorySlug,
			initialPosts,
			initialTotal,
		})

	// Flatten all pages into a single array of posts
	const allPosts = data?.pages.flatMap((page) => page.posts) || []

	if (isError) {
		return (
			<div className="col-span-full text-center py-12">
				<p className="text-xl text-red-500">{t('errorLoading')}</p>
			</div>
		)
	}

	return (
		<>
			<div className="blog-page__grid">
				{allPosts.length > 0 ? (
					allPosts.map((post) => (
						<BlogCard key={post._id} post={post} lang={language} />
					))
				) : (
					<BlogEmptyState />
				)}
			</div>

			{hasNextPage && (
				<div className="blog-page__view-more-container">
					{isFetchingNextPage ? (
						<LoadingDots
							className="blog-page__view-more-loading"
							ariaLabel={t('loading')}
						/>
					) : (
						<Button
							type="button"
							name={t('viewMore')}
							className="blog-page__view-more-btn"
							rounded={false}
							onClick={() => fetchNextPage()}
						>
							{t('viewMore')}
						</Button>
					)}
				</div>
			)}
		</>
	)
}

export default BlogPostsList

